import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ChatProvider } from "./contexts/ChatContext";
import ChatPanel from "./components/ChatPanel";
import ChatHeads from "./components/ChatHeads";
import TaskToast from "./components/TaskToast";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import NewClient from "./pages/NewClient";
import ClientDetail from "./pages/ClientDetail";
import FinanceOverview from "./pages/FinanceOverview";
import AddStatement from "./pages/AddStatement";
import CompanyFinanceView from "./pages/CompanyFinanceView";
import Editor from "./pages/Editor";
import Templates from "./pages/Templates";
import History from "./pages/History";
import Users from "./pages/Users";
import Tasks from "./pages/Tasks";
import NewTask from "./pages/NewTask";

// Lands on /clients if dashboard isn't enabled, otherwise /
const DashboardOrFallback = () => {
  const { has } = useAuth();
  if (has('dashboard')) return <Dashboard />;
  if (has('clients'))   return <Navigate to="/clients" replace />;
  if (has('finance'))   return <Navigate to="/finance" replace />;
  if (has('templates')) return <Navigate to="/templates" replace />;
  if (has('history'))   return <Navigate to="/history" replace />;
  return <Navigate to="/tasks" replace />;
};

// Tasks-only guard — every logged-in user can use Tasks (always-on),
// but assigning is gated by `can_assign_tasks`.
const RequireAssigner = ({ children }) => {
  const { user } = useAuth();
  const canAssign = user?.role === 'super_admin' || !!user?.can_assign_tasks;
  if (!canAssign) return <Navigate to="/tasks" replace />;
  return children;
};

const Shell = ({ children }) => (
  <AppShell>{children}</AppShell>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <ChatProvider>
              <Routes>
              <Route path="/login" element={<Login />} />

              <Route path="/" element={
                <ProtectedRoute><Shell><DashboardOrFallback /></Shell></ProtectedRoute>
              } />
              <Route path="/clients" element={
                <ProtectedRoute feature="clients"><Shell><Clients /></Shell></ProtectedRoute>
              } />
              <Route path="/clients/new" element={
                <ProtectedRoute feature="clients"><Shell><NewClient /></Shell></ProtectedRoute>
              } />
              <Route path="/clients/:id" element={
                <ProtectedRoute feature="clients"><Shell><ClientDetail /></Shell></ProtectedRoute>
              } />
              <Route path="/finance" element={
                <ProtectedRoute feature="finance"><Shell><FinanceOverview /></Shell></ProtectedRoute>
              } />
              <Route path="/finance/add" element={
                <ProtectedRoute feature="finance"><Shell><AddStatement /></Shell></ProtectedRoute>
              } />
              <Route path="/finance/company/:id" element={
                <ProtectedRoute feature="finance"><Shell><CompanyFinanceView /></Shell></ProtectedRoute>
              } />
              <Route path="/editor" element={
                <ProtectedRoute feature="clients"><Shell><Editor /></Shell></ProtectedRoute>
              } />
              <Route path="/templates" element={
                <ProtectedRoute feature="templates"><Shell><Templates /></Shell></ProtectedRoute>
              } />
              <Route path="/history" element={
                <ProtectedRoute feature="history"><Shell><History /></Shell></ProtectedRoute>
              } />

              {/* Tasks — always on for any authenticated user. /tasks/assign is
                  kept as an alias that redirects back to /tasks (old bookmarks). */}
              <Route path="/tasks" element={
                <ProtectedRoute><Shell><Tasks /></Shell></ProtectedRoute>
              } />
              <Route path="/tasks/assign" element={<Navigate to="/tasks" replace />} />
              <Route path="/tasks/new" element={
                <ProtectedRoute><RequireAssigner><Shell><NewTask /></Shell></RequireAssigner></ProtectedRoute>
              } />

              <Route path="/users" element={
                <ProtectedRoute superAdminOnly><Shell><Users /></Shell></ProtectedRoute>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <ChatPanel />
            <ChatHeads />
            <TaskToast />
            </ChatProvider>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
